/**
 * Projection — turn a questionnaire definition into a graph the canvas can draw,
 * plus the static issues found on the way.
 *
 * ## The canvas is a PROJECTION of the rules, not their storage
 *
 * This is the load-bearing idea of the whole surface. The live data has exactly ONE
 * node type: a question (`QuestionnaireNode`). There is no "decision" node and no
 * "eligibility tag" node — a threshold test lives inside an expression string, and
 * eligibility lives in `programRules[]` hanging off a question. The flow chart the
 * business reads, on the other hand, has all three.
 *
 * So we DERIVE the extra nodes here and mark them `derived: true`. Derived nodes are
 * rendered, inspected and highlighted like any other, but they cannot be created,
 * moved or deleted on their own: they are a view of a rule, and the rule is the
 * thing that exists. Skipping this distinction is how a canvas editor ends up able
 * to draw a node the engine has no field for.
 *
 * ## Layout is deterministic, on purpose
 *
 * No dagre, no elkjs. Rank = longest distance from the entry node, column = order
 * within the rank, derived nodes sit to the right of their owner. The reason is not
 * bundle size: an auto-layout reshuffles coordinates whenever the graph changes, and
 * the single most important review this surface has to support is "diff the draft
 * against what is live". If the two renders of a 15-node tree do not line up, that
 * diff is unreadable. Longest-path ranking also makes merge points (Q1.1.3, where
 * the Global and China branches rejoin) land BELOW both branches rather than beside
 * one of them.
 */
import { dagLayout } from './dagLayout';
import type {
  ExpressionRule,
  ProgramExpressionRule,
  QuestionnaireDefinition,
  QuestionnaireNode,
} from '../types';

// ── Geometry ────────────────────────────────────────────────────────────────
// Exported so the canvas can size its viewport without re-deriving them.
/*
 * Geometry, measured off the design mock (Figma `cpa-liu`, 2026-10-29; the mock is a
 * screenshot, so these come from pixels — 531x126 at 2x with 55/56 gaps → 266x63/28).
 * Rounded to even numbers. The first cut of this file was noticeably looser
 * (76 high, 48/40 gaps), which added ~370px of height to the 15-row q_1_1 spine.
 */
export const NODE_W = 266;
export const NODE_H = 64;
export const GRANT_W = 240;
/**
 * Card height, computed from CONTENT rather than fixed.
 *
 * A card is just the question now, and the question wraps. A single constant therefore
 * either lies about the two-line cards (which is what happened: the layout reserved
 * 64px for cards rendering at ~80, so connectors had nowhere to be drawn) or wastes a
 * line of space on every one-liner.
 *
 * Estimated arithmetically, not measured in the DOM, because measuring means a second
 * pass after paint: `projectQuestionnaire` would stop being pure and synchronous, and
 * the draft-vs-live diff plus every geometry test rest on it being both. The estimate
 * only has to be right about the LINE COUNT, and the card clamps to the same
 * CARD_TITLE_MAX_LINES this does, so it cannot be off by more than one line.
 *
 * ⚠️ This budget must name EVERY row the card renders — today the kind chip, the 「问题」
 * label, the wrapped title, the divider, the 「答案」 label and the answer rows — and
 * assumes the title is clamped to CARD_TITLE_MAX_LINES. `TreeCanvas` is what makes that
 * true: it sets `-webkit-line-clamp` FROM this constant, and `.cpa-node` clips. Adding a
 * row to the card without adding it here makes the card overflow its reserved height and
 * the clip silently cuts content in half; leaving a row out here after removing it from
 * the card wastes a line on every node. That drift has happened twice. Keep them in step.
 */
const CARD_PAD_X = 12;
const CARD_PAD_Y = 10 * 2;
const CARD_BORDER_Y = 2;
/**
 * The answer-kind chip above the title: 15px line box + 2px border + 4px margin.
 *
 * This was missing from the budget when the chip was added, and a two-line question
 * then overflowed its reserved height by exactly this much — `overflow: hidden` on the
 * card sliced the second line in half. The same drift as the original bug, in the
 * other direction: the renderer grew a row the layout did not know about.
 *
 * It then said 19 for a while, having forgotten the chip's own `border: 1px solid
 * transparent` — a transparent border still occupies space, so every card was 2px
 * short and the last row of options lost its bottom edge.
 */
const CARD_KIND_H = 21;
const CARD_TITLE_LINE_H = 19;
/**
 * Line cap for the question text.
 *
 * This was 2, which clamped the long questions — `Q1.1.3.1`'s 「此客户是否计划在60天内签署
 * 新PPA、续签现有PPA、终止现有PPA…」 ran to an ellipsis at the narrow end of the width
 * range. A card whose question ends in 「…」 right above a list of answers reads as one
 * run-on block, which is what made the question and the answers look mixed together.
 *
 * Six covers every live question at CARD_MIN_W with room to spare; past that the budget
 * still has to stop somewhere, or one pathological question makes a card taller than the
 * viewport. Exported because the clamp lives in CSS and the budget here assumes it —
 * `TreeCanvas` sets `-webkit-line-clamp` from this constant so there is one number.
 */
export const CARD_TITLE_MAX_LINES = 6;
/**
 * A section label row (「问题」/「答案」): 11px line box + 3px margin.
 *
 * The labels exist because the card stacks two different kinds of text — one question
 * and N answers — in the same column, and at a glance the first answer chip read as a
 * continuation of the question. Naming the two sections costs two short rows and is
 * cheaper than the alternatives (colour alone does not say WHAT the block is, and
 * indentation does not survive a narrow card).
 */
const CARD_LABEL_H = 14;
/** The hairline above the answers block: 8px margin + 1px rule. */
const CARD_DIVIDER_H = 9;
/** One row of option chips: 15px line box + 2px border + 3px gap. */
const CARD_OPT_ROW_H = 20;
/**
 * One spare row, on any card that has options.
 *
 * `chipWidth` estimates text width per character; it cannot be exact, because the real
 * width depends on the font the browser actually resolved and on kerning. When the
 * estimate is one pixel too small on a row that was going to be full, the browser wraps
 * a chip the budget did not reserve — and the card's `overflow: hidden` eats it, so an
 * option silently disappears from the card while the panel still lists it. That is
 * precisely the bug this file has now produced twice.
 *
 * A spare row makes the failure mode 20px of whitespace instead of a lost option. On a
 * graph this size that is invisible; a missing option is not. It is NOT a licence to
 * stop counting rows — the budget above still has to name every one.
 */
const CARD_OPT_SLACK_ROWS = 1;
/** Gap between the question and the answers below it. */
const CARD_OPT_GAP = 5;
/**
 * Row cap for answers on a card.
 *
 * This was 3, and three was wrong: `Q1.1.3.1` has six options, so the card showed a/b/c
 * and dropped the rest. Editing or adding option `f` in the panel then changed nothing
 * on the card — indistinguishable from the canvas not updating at all, which is exactly
 * how it was reported. The card is the only place an author sees what a question asks,
 * so hiding half the answer set to save vertical space trades the thing the card is for
 * against space the layout has plenty of.
 *
 * So the cap is now high enough that no live question reaches it (the widest is six) and
 * it exists only to stop a pathological 40-option question from drawing a card taller
 * than the viewport. Overflow past it gets its OWN row for the "+N" chip — see the
 * reserve in `cardSize`, because the chip previously wrapped onto the first clipped row
 * and vanished with it.
 */
const CARD_OPT_MAX_ROWS = 12;

/**
 * Width bounds.
 *
 * Cards are NOT a uniform width: a yes/no question needs far less room than one with
 * six twenty-character answers, and forcing both to 266px either wastes half of one or
 * truncates the other. d3-dag's `nodeSize` is per node, so a layered layout handles
 * variable widths natively — and once parents are centred over their children there is
 * no column grid for a uniform width to line up with anyway.
 *
 * The bounds exist because fully fluid is worse than either: a 600px card beside a
 * 200px one reads as two different kinds of object, and the whole graph's width is the
 * sum of its widest row.
 */
const CARD_MIN_W = 232;
const CARD_MAX_W = 392;
/** Floor, so a one-line question with no answers does not render as a chip. */
const CARD_MIN_H = 56;

/**
 * Rough advance width of one character at the title's 13px.
 *
 * CJK is full-width; Latin averages about half. The questionnaire is mostly Chinese
 * with embedded English terms ("Gross Spend ARR ($K)"), so counting the two separately
 * is what keeps the line estimate honest for both.
 */
function textWidth(text: string, cjk = 13, latin = 7): number {
  let w = 0;
  for (const ch of text) w += /[\u3000-\u9fff\uff00-\uffef]/.test(ch) ? cjk : latin;
  return w;
}

/**
 * An option chip's width: its label at 11px, plus 12px padding and 2px border.
 *
 * Rounded UP, and the per-character figures are the generous end of the range. The
 * asymmetry is deliberate: over-estimating a chip costs a few px of card width, while
 * under-estimating it makes the browser wrap a row the budget did not reserve, and the
 * card clips it. One is invisible, the other loses content.
 */
const chipWidth = (label: string): number => Math.ceil(textWidth(label, 11.5, 6.4)) + 14;

/** The labels shown as chips under the question, in display order. */
function optionLabels(n: QuestionnaireNode): string[] {
  const k = questionKindOf(n);
  if (k !== 'single' && k !== 'multi') return [];
  return (n.answerOptions ?? []).map((o) => o.optionText.cn || o.optionText.en || o.value);
}

/** Greedy line-packing — the same thing flex-wrap does, so the two agree. */
function packRows(widths: number[], available: number): number {
  if (widths.length === 0) return 0;
  let rows = 1;
  let used = 0;
  for (const w of widths) {
    const need = used === 0 ? w : used + 3 + w;
    if (need <= available) used = need;
    else {
      rows++;
      used = w;
    }
  }
  return rows;
}

export interface CardSize {
  width: number;
  height: number;
  /** How many option rows the card reserves, including the "+N" row when there is one. */
  optionRows: number;
  /**
   * How many options the card may RENDER.
   *
   * The renderer slices to this instead of drawing all of them and relying on a clip.
   * Relying on the clip put the "+N" chip last in the flex flow — i.e. after every
   * chip, on the last row of the full set rather than the reserved row — so the one
   * chip whose job was to say "there is more" was below the cut and invisible.
   */
  shownOptions: number;
  /** Options not drawn, summarised as "+N". */
  hiddenOptions: number;
  /** Lines the question takes, after the CARD_TITLE_MAX_LINES clamp. */
  titleLines: number;
  /** True when the clamp cut the question short — a test asserts no live question does. */
  titleClamped: boolean;
}

/**
 * How big this card has to be, from its content.
 *
 * Width first, because it decides how the text wraps; then height at that width.
 * Estimated arithmetically rather than measured in the DOM: measuring means a second
 * pass after paint, and `projectQuestionnaire` has to stay pure and synchronous — the
 * draft-vs-live diff and every geometry test rest on it.
 *
 * ⚠️ This budget must name EVERY row the card renders — the kind chip, the title, the
 * answer rows. `TreeCanvas.css` clamps the title to CARD_TITLE_MAX_LINES and clips the
 * card, so a row added to the card and not to this budget gets silently sliced in half,
 * and a row removed from the card but left here wastes space on every node. That drift
 * has happened twice. Keep them in step.
 */
export function cardSize(n: QuestionnaireNode): CardSize {
  const title = n.questionText.cn || n.questionText.en;
  const labels = optionLabels(n);
  const chips = labels.map(chipWidth);

  /*
   * Width is driven by the widest answer chip, not by the question: the question wraps
   * and the chip does not. A yes/no card lands on the floor; a six-long-answer card
   * widens until the ceiling.
   */
  const widest = chips.length > 0 ? Math.max(...chips) : 0;
  const width = Math.max(CARD_MIN_W, Math.min(CARD_MAX_W, widest + CARD_PAD_X * 2));
  const inner = width - CARD_PAD_X * 2;

  const wantTitleLines = Math.max(1, Math.ceil(textWidth(title) / inner));
  const titleLines = Math.min(CARD_TITLE_MAX_LINES, wantTitleLines);
  const wantRows = packRows(chips, inner);
  const chipRows = Math.min(CARD_OPT_MAX_ROWS, wantRows);

  // How many chips actually fit in the rows we are drawing.
  let shown = 0;
  let rows = 1;
  let used = 0;
  for (const w of chips) {
    const need = used === 0 ? w : used + 3 + w;
    if (need > inner) {
      rows++;
      used = w;
      if (rows > chipRows) break;
    } else used = need;
    shown++;
  }

  const hiddenOptions = Math.max(0, chips.length - shown);
  /*
   * The "+N" chip gets a row of its own rather than sharing the last one. It is a chip
   * in the same flex-wrap container, so letting it wrap put it on the first row past
   * the clip — leaving a card that silently showed a prefix of its options.
   */
  const optionRows = chipRows + (hiddenOptions > 0 ? 1 : 0);

  const height = Math.max(
    CARD_MIN_H,
    CARD_PAD_Y +
      CARD_BORDER_Y +
      CARD_KIND_H +
      // 「问题」
      CARD_LABEL_H +
      titleLines * CARD_TITLE_LINE_H +
      // The options block carries its own divider and 「选项」 label, plus the slack.
      (optionRows > 0
        ? CARD_OPT_GAP +
          CARD_DIVIDER_H +
          CARD_LABEL_H +
          optionRows * CARD_OPT_ROW_H +
          CARD_OPT_SLACK_ROWS * CARD_OPT_ROW_H
        : 0),
  );

  return {
    width,
    height,
    optionRows,
    shownOptions: shown,
    hiddenOptions,
    titleLines,
    titleClamped: wantTitleLines > titleLines,
  };
}

/** Height only — kept for callers that have a title and no node. */
export function cardHeight(title: string): number {
  return cardSize({
    nodeId: '',
    questionText: { id: '', en: title, cn: title },
    questionType: 'INPUT_TEXT',
    isLastNode: true,
  }).height;
}

export const COL_GAP = 48;
/**
 * Vertical gap between rows. Same reasoning as COL_GAP, and the tighter of the two
 * constraints: the connector, its arrowhead AND its label all live in this gap.
 */
export const ROW_GAP = 56;
/**
 * Horizontal spacing between detour lanes for rank-skipping edges, and the gutter
 * reserved on the left to hold them. See {@link assignLanes}.
 */
export const LANE_GAP = 26;

/** Canvas node kinds. Only `question` corresponds to a stored record. */
export type CpaNodeKind = 'question' | 'grant';

/**
 * The kind of answer a question takes, for the canvas to distinguish cards by.
 *
 *   single | multi | number | text | custom
 *
 * Note what is NOT a kind: an AMOUNT. `$K` is a unit on a number, so it is `number`
 * plus `unit: '$K'` — making it a fifth kind would mean a sixth for `%`, a seventh for
 * the next unit, and four of them differing only in a label. A percentage is likewise
 * `number` + `unit: '%'`, and additionally `scale: 0.01` because that one changes the
 * stored value (see `QuestionnaireNode.scale`).
 *
 * `custom` IS its own kind, because it is not a control with a different label — it is
 * a different component, with its own data source and its own failure modes. The SFDC
 * account picker calls `gcr-company-service-client`, switches between a searchable list
 * and a free-text id, and validates remotely. Nothing about that is a number with a
 * unit.
 */
export type QuestionKind = 'single' | 'multi' | 'number' | 'text' | 'custom';

export function questionKindOf(n: QuestionnaireNode): QuestionKind {
  // A named component wins: it decides the whole control, not just its label.
  if (n.uiComponent) return 'custom';
  if (n.questionType === 'LIST_SELECTION') return n.multiSelect ? 'multi' : 'single';
  return n.questionType === 'INPUT_NUMBER' ? 'number' : 'text';
}

export interface CpaNode {
  /** Canvas id. Question nodes reuse `nodeId`; derived ones get a `<nodeId>::grant::<i>` id. */
  id: string;
  kind: CpaNodeKind;
  /** false only for question nodes. */
  derived: boolean;
  position: { x: number; y: number };
  /**
   * The height this card was laid out WITH. The renderer must apply it, not just
   * treat it as a minimum: the layout reserves exactly this much vertical room, and
   * a card that renders taller eats the gap its own connector needs. That drift is
   * what made the first tidy layout look like it had no edges at all — cards declared
   * NODE_H (64) and rendered up to ~80 inside a 92px pitch.
   */
  height: number;
  /**
   * Card width. Not a constant: see `cardSize` — a yes/no question and one with six
   * long answers need different room, and the layered layout places variable sizes
   * natively.
   */
  width: number;
  /** Answer rows the card draws, and how many answers that leaves out. */
  optionRows: number;
  /** How many options the card may draw; the renderer slices to this. See `CardSize`. */
  shownOptions: number;
  hiddenOptions: number;
  /**
   * Row index, 0 at the top. Exposed because it is the only honest way to assert that
   * a row stays top-aligned: grouping cards by their already-converted `position.y`
   * makes any such assertion circular.
   */
  layer: number;
  data: {
    /** The owning question's `nodeId` — the same value for a question and its derived nodes. */
    ownerNodeId: string;
    title: string;
    subtitle?: string;
    /** Set on question nodes. */
    node?: QuestionnaireNode;
    /** Set on question nodes — what kind of answer it takes. */
    questionKind?: QuestionKind;
    /** Set on grant nodes: which rule of the owner this projects. */
    ruleIndex?: number;
    rule?: ProgramExpressionRule;
    /** Issue ids anchored on this node, so the canvas can badge it. */
    issueIds: string[];
  };
}

export interface CpaEdge {
  id: string;
  source: string;
  target: string;
  /** `always` = `nextNodeId`; `when` = a `nextQuestionRules` entry; `grant` = to a derived node. */
  kind: 'always' | 'when' | 'grant';
  label?: string;
  /**
   * How many layout rows this edge jumps. 1 = adjacent rows.
   *
   * Anything above 1 passes THROUGH the rows in between, and since nodes render above
   * edges, a straight line would be hidden behind whatever card sits in the way. The
   * live q_1_1 has exactly this case: `Q1.1.4 --否--> Q1.1.5` skips `Q1.1.4.1`.
   */
  rowSpan?: number;
  /** Detour lane for a skipping edge; `undefined` means draw it straight. */
  lane?: number;
  /** Absolute x of that lane, so the edge renderer needs no layout knowledge. */
  laneX?: number;
  /**
   * 1-based position of this edge among its source's conditional edges, and how many
   * there are.
   *
   * `nextQuestionRules` is ORDERED and the engine takes the FIRST rule that evaluates
   * true, so two edges out of one node are not symmetric — an earlier one shadows a
   * later one. That ordering is a property of the edge SET, not of either node, which
   * makes the line the only correct place to show it. It was invisible on the canvas
   * before, and the risk is one the editor itself introduces: "add a branch" appends a
   * `true` catch-all, and from that moment position decides whether a branch can ever
   * fire.
   */
  ruleIndex?: number;
  ruleCount?: number;
  /**
   * Human-readable condition, e.g. `是` instead of `metric['Q_1_1_4'] === 'a'`.
   *
   * undefined when the expression is not a shape we can render in a word — the raw
   * text is then shown truncated. Edge-label real estate is one short phrase; the long
   * expressions (the TCV one is 60 characters) never fitted.
   */
  displayLabel?: string;
  /** The trailing `true` rule — semantically "otherwise", not the literal `true`. */
  isDefaultBranch?: boolean;
  /**
   * This branch can never be taken: it sits after a catch-all, or repeats an earlier
   * condition verbatim.
   *
   * Marked on the EDGE, not the target node — the target is often perfectly reachable
   * through a different edge, so flagging the node would point at the wrong thing.
   */
  isDeadBranch?: boolean;
}

/**
 * `info` exists for one specific situation: a check that ran and could NOT decide.
 *
 * Silence would be wrong (the reader would assume it was verified) and a warning would
 * be wrong too (nothing is known to be broken). The honest third state is "checked,
 * undecidable, look at it yourself".
 */
export type IssueSeverity = 'error' | 'warning' | 'info';

export interface Issue {
  id: string;
  severity: IssueSeverity;
  /** Stable code so tests and telemetry do not pin on prose. */
  code:
    | 'both-edge-kinds'
    | 'dangling-edge-target'
    | 'dead-end'
    | 'unreachable'
    | 'missing-qualified-id'
    | 'unknown-metric-ref'
    | 'ref-not-dominating'
    | 'ref-group-uncovered'
    | 'unknown-program-name'
    | 'numeric-op-on-text-node'
    | 'dead-branch'
    | 'cycle'
    | 'branch-overlap'
    | 'branch-overlap-unknown';
  /** Canvas node the issue is anchored on, when there is one. */
  nodeId?: string;
  message: string;
}

export interface Projection {
  nodes: CpaNode[];
  edges: CpaEdge[];
  issues: Issue[];
  /** Entry node id, resolved the same way the service resolves it. */
  entryNodeId: string | undefined;
  width: number;
  height: number;
  /**
   * Edge crossings between adjacent rows, after ordering.
   *
   * Reported rather than merely minimised so "is this layout good" has a number
   * instead of an opinion. Skipping edges are excluded — they are routed in gutter
   * lanes and do not cross the spine.
   */
  crossings: number;
}

/**
 * How columns within a row are ordered.
 *
 *  - `source` — the order the nodes appear in the definition file. Stable under edits:
 *    adding a node never re-shuffles its siblings, which is what keeps a draft and the
 *    live version visually comparable.
 *  - `tidy` — the median heuristic from layered graph drawing (the ordering phase of
 *    Sugiyama), run as alternating sweeps and keeping the best result by crossing
 *    count. Produces fewer crossings on a wide graph, at the cost of that stability: a
 *    small edit can legitimately re-order a whole row.
 *
 * Deliberately NOT "optimal": minimising crossings in a layered drawing is NP-hard
 * even for two layers, so every tool ships a heuristic. Calling it `tidy` rather than
 * `best` is the honest name.
 */
export type Ordering = 'source' | 'tidy';

/**
 * The 28 program names the engine will match against, verbatim from
 * `SalesRecommendationHub/src/main/resources/program/ProgramNameStandard`.
 *
 * Checked in as a literal because that resource is not served by any API — the
 * authoritative list at runtime is whatever rows exist in the `InvestmentProgramDetail`
 * DynamoDB table, which this surface will read once it has a client. Until then this
 * is the catalogue, and it is enough to catch the casing drift that silently disabled
 * one of the two live Q1.1.7 rules.
 */
export const PROGRAM_NAMES: readonly string[] = [
  'GCR Activate',
  'GCR Hot Startup',
  'Greenfield FastStart',
  'SCE-led Cross Service PPA',
  'Seller-led Cross Service PPA',
  'SCE-led Service Specific PPA',
  'Seller-led Service Specific PPA',
  'DB & Analytics MAP',
  'VMware MAP',
  'Mainframe Must Win',
  'MAP for SAP & Oracle',
  'MAP 2.0',
  'MAP Lite',
  'GCR MAP',
  'AWS Global Passport (ISV)',
  'SPP Refund or SP Benefit',
  'VIP Funding',
  'Compete Funding',
  'T2K Winroom',
  'ACDC',
  'IVT Solution PoC',
  'GenAI Service PoC',
  'Non GenAI Service PoC',
  'Partner Solution PoC',
  'General Platform Adoption PoC (Sales PoC)',
  'Good Will Credit',
  'China Regions acquire new',
  'R-PPA',
];

/**
 * Entry node, resolved exactly as `QuestionnaireGenerator.enhance()` does: the node
 * flagged `isFirstNode`, else the first in array order.
 *
 * The fallback is not defensive coding — it is the live behaviour. Four of the five
 * resource files set the flag; the surface must not disagree with the engine about
 * where a walk starts.
 */
export function entryNodeOf(nodes: QuestionnaireNode[]): QuestionnaireNode | undefined {
  return nodes.find((n) => n.isFirstNode) ?? nodes[0];
}

/**
 * Render a condition as a short phrase, or `undefined` when it has no short form.
 *
 * Only the two shapes that actually dominate the live rules are handled — equality
 * against an option value, and `includes` for a multi-select — plus the `true`
 * catch-all. Everything else (arithmetic, `&&` chains, `hasKey`) has no honest
 * one-word form, so the caller falls back to truncated raw text rather than this
 * inventing a paraphrase that could misstate the rule.
 */
export function humanCondition(
  expression: string,
  optionLabelOf: (qualifiedId: string, value: string) => string | undefined,
): string | undefined {
  const e = expression.trim();
  if (e === 'true') return '否则';

  const eq = /^metric\[\s*'([^']+)'\s*\]\s*={2,3}\s*'([^']*)'$/.exec(e);
  if (eq) return optionLabelOf(eq[1], eq[2]) ?? eq[2];

  const inc = /^metric\[\s*'([^']+)'\s*\]\.includes\(\s*'([^']*)'\s*\)$/.exec(e);
  if (inc) {
    const label = optionLabelOf(inc[1], inc[2]) ?? inc[2];
    return `含「${label}」`;
  }
  return undefined;
}

/** Every `metric['X']` name an expression reads. */
export function metricRefsOf(expression: string): string[] {
  const out: string[] = [];
  const re = /metric\[\s*'([^']+)'\s*\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(expression)) !== null) out.push(m[1]);
  return [...new Set(out)];
}

/** Every `hasKey('X')` name an expression probes — i.e. "was program X already granted". */
export function hasKeyRefsOf(expression: string): string[] {
  const out: string[] = [];
  const re = /hasKey\(\s*'([^']+)'\s*\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(expression)) !== null) out.push(m[1]);
  return [...new Set(out)];
}

/**
 * Outgoing edges of a node, honouring the engine's precedence.
 *
 * `nextNodeId` SHORT-CIRCUITS `nextQuestionRules` — enhance() installs
 * StaticNextNodeCalculator and the conditional rules are never consulted. Returning
 * both here would draw edges the engine cannot take; the `both-edge-kinds` issue is
 * what tells the author about it instead.
 */
function outEdgesOf(n: QuestionnaireNode): { target: string; kind: 'always' | 'when'; label?: string }[] {
  if (n.isLastNode) return [];
  if (n.nextNodeId) return [{ target: n.nextNodeId, kind: 'always' }];
  return (n.nextQuestionRules ?? []).map((r: ExpressionRule) => ({
    target: r.nextQuestionId,
    kind: 'when' as const,
    label: r.expression,
  }));
}

/** Nodes reachable from the entry node. */
function reachableFrom(entry: string | undefined, adj: Map<string, string[]>): Set<string> {
  const seen = new Set<string>();
  if (!entry) return seen;
  const stack = [entry];
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const nxt of adj.get(cur) ?? []) if (!seen.has(nxt)) stack.push(nxt);
  }
  return seen;
}

/**
 * Dominator sets over the reachable sub-graph: `Dom(n)` = nodes on EVERY path from
 * the entry to `n` (including `n`).
 *
 * This is what answers "is the question this rule reads guaranteed to have been
 * asked by the time the rule runs". In q_1_1 the Global and China branches are
 * mutually exclusive, so `Q_1_1_2_a1` does NOT dominate anything past the merge at
 * Q1.1.3 — which is precisely why the live ISV and ACDC rules have to reach for
 * `metric['..._a1'] || metric['..._b1']`, and why the ACDC one gets it wrong.
 *
 * Plain fixpoint iteration. The biggest live tree is 15 nodes, so the O(n²)
 * worst case is irrelevant and clarity wins over Lengauer–Tarjan.
 */
function dominators(
  entry: string | undefined,
  ids: string[],
  preds: Map<string, string[]>,
  reachable: Set<string>,
): Map<string, Set<string>> {
  const dom = new Map<string, Set<string>>();
  if (!entry) return dom;
  const universe = ids.filter((id) => reachable.has(id));
  for (const id of universe) dom.set(id, new Set(id === entry ? [entry] : universe));
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of universe) {
      if (id === entry) continue;
      const ps = (preds.get(id) ?? []).filter((p) => reachable.has(p));
      // A node with no reachable predecessor keeps the full universe; it cannot
      // happen inside the reachable set except for the entry, which we skip.
      let next: Set<string> | null = null;
      for (const p of ps) {
        const dp = dom.get(p)!;
        if (next === null) {
          next = new Set<string>(dp);
        } else {
          const acc: Set<string> = next;
          next = new Set<string>([...acc].filter((x) => dp.has(x)));
        }
      }
      const merged: Set<string> = next ?? new Set<string>();
      merged.add(id);
      const prev = dom.get(id)!;
      if (prev.size !== merged.size || [...merged].some((x) => !prev.has(x))) {
        dom.set(id, merged);
        changed = true;
      }
    }
  }
  return dom;
}

/**
 * Post-dominator sets: `PostDom(n)` = nodes on EVERY path from `n` to a terminal
 * (including `n`).
 *
 * Needed because the two rule families are evaluated at DIFFERENT times, and so
 * have different answers available:
 *
 *  - `nextQuestionRules` / `autoAnswerRules` run while the user is standing on the
 *    node. Only answers already given exist → the basis is {@link dominators}.
 *  - `programRules` run at SUBMIT time, when QuestionnaireProcessor walks the whole
 *    tree with the complete answer set. Anything on the path the user actually took
 *    exists, including questions asked LATER → the basis is Dom ∪ PostDom.
 *
 * Without this distinction the checker would flag the live TCV rule on Q1.1.2_a2 for
 * reading `metric['Q_1_1_9']` (support years, asked seven questions later), which is
 * perfectly valid at submit time. Same fixpoint, run on the reversed graph from the
 * terminal nodes.
 */
function postDominators(
  ids: string[],
  adj: Map<string, string[]>,
  reachable: Set<string>,
): Map<string, Set<string>> {
  const universe = ids.filter((id) => reachable.has(id));
  const pdom = new Map<string, Set<string>>();
  // Terminals: no successor inside the reachable set (isLastNode, or a dead end —
  // a dead end is already reported as an error, and treating it as a terminal keeps
  // this pass from depending on that error being fixed first).
  const succs = (id: string) => (adj.get(id) ?? []).filter((s) => reachable.has(s));
  const terminals = universe.filter((id) => succs(id).length === 0);
  for (const id of universe) pdom.set(id, new Set(terminals.includes(id) ? [id] : universe));
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of universe) {
      if (terminals.includes(id)) continue;
      let next: Set<string> | null = null;
      for (const s of succs(id)) {
        const ds = pdom.get(s)!;
        if (next === null) {
          next = new Set<string>(ds);
        } else {
          const acc: Set<string> = next;
          next = new Set<string>([...acc].filter((x) => ds.has(x)));
        }
      }
      const merged: Set<string> = next ?? new Set<string>();
      merged.add(id);
      const prev = pdom.get(id)!;
      if (prev.size !== merged.size || [...merged].some((x) => !prev.has(x))) {
        pdom.set(id, merged);
        changed = true;
      }
    }
  }
  return pdom;
}

/**
 * Does the SET `cut` lie on every path the engine can take through `owner`?
 *
 * The single-node version of this question is dominance. The set version is what a
 * hand-written fallback needs: the live rules reach across the Global / China split
 * with `metric['..._a1'] || metric['..._b1']`, and neither operand dominates on its
 * own — together they do, because the two branches are mutually exclusive. Checking
 * them individually reports 17 warnings on q_1_1 that a careful author has already
 * handled, which would train people to ignore the panel.
 *
 * Implemented by deletion rather than by intersecting dominator sets: delete `cut`
 * from the graph and ask whether the engine can still get to (and, for submit-time
 * rules, through) `owner`. If it cannot, every surviving path met the fallback.
 */
function cutCoversOwner(
  cut: Set<string>,
  owner: string,
  entry: string | undefined,
  adj: Map<string, string[]>,
  reachable: Set<string>,
  basis: 'before' | 'whole-walk',
): boolean {
  if (!entry || cut.has(owner)) return true;
  const walk = (from: string) => {
    const seen = new Set<string>();
    const stack = [from];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur) || cut.has(cur) || !reachable.has(cur)) continue;
      seen.add(cur);
      for (const n of adj.get(cur) ?? []) if (!seen.has(n)) stack.push(n);
    }
    return seen;
  };
  const fromEntry = walk(entry);
  // Cannot even arrive at `owner` without crossing the cut → covered.
  if (!fromEntry.has(owner)) return true;
  if (basis === 'before') return false;
  // Submit-time rules also see answers given AFTER the owner, so a fallback placed
  // downstream still counts. Covered only if every continuation hits the cut, i.e.
  // no terminal survives downstream of `owner`.
  const onward = walk(owner);
  const survivingTerminal = [...onward].some(
    (id) => (adj.get(id) ?? []).filter((s) => reachable.has(s) && !cut.has(s)).length === 0,
  );
  return !survivingTerminal;
}

/**
 * Longest distance from the entry to each node, which is the row a node is drawn on.
 *
 * Longest rather than shortest so a merge point sits below BOTH incoming branches
 * instead of level with the shorter one. Computed by relaxation with a visit cap:
 * the data is a DAG today, but a draft can contain a cycle (the editor's job is to
 * report one, not to hang on it), so the cap is the safety net.
 */
function longestRanks(
  entry: string | undefined,
  adj: Map<string, string[]>,
  reachable: Set<string>,
): Map<string, number> {
  const rank = new Map<string, number>();
  if (!entry) return rank;
  for (const id of reachable) rank.set(id, 0);
  const cap = reachable.size + 1;
  for (let pass = 0; pass < cap; pass++) {
    let changed = false;
    for (const id of reachable) {
      const base = rank.get(id)!;
      for (const nxt of adj.get(id) ?? []) {
        if (!reachable.has(nxt)) continue;
        if (rank.get(nxt)! < base + 1) {
          rank.set(nxt, base + 1);
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
  return rank;
}

/**
 * Give every rank-skipping edge a detour lane, reusing a lane whenever two skips do
 * not overlap vertically.
 *
 * Interval-graph colouring: the edges are intervals over layout rows, and two that do
 * not overlap can share a lane without crossing. Processed in a stable order (top row
 * first, then by id) so the assignment is deterministic — the same property the rest
 * of the layout is built on, since a lane that reshuffles would break the draft-vs-live
 * comparison just as a moving node would.
 */
function assignLanes(
  spans: { id: string; from: number; to: number }[],
): { laneOf: Map<string, number>; laneCount: number } {
  const ordered = [...spans].sort((a, b) => a.from - b.from || a.id.localeCompare(b.id));
  const laneOf = new Map<string, number>();
  /** Per lane, the intervals already placed in it. */
  const lanes: { from: number; to: number }[][] = [];
  for (const s of ordered) {
    const lo = Math.min(s.from, s.to);
    const hi = Math.max(s.from, s.to);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      // Touching endpoints are fine; only a real overlap forces a new lane.
      if (lanes[i].every((x) => hi <= x.from || lo >= x.to)) {
        lanes[i].push({ from: lo, to: hi });
        laneOf.set(s.id, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([{ from: lo, to: hi }]);
      laneOf.set(s.id, lanes.length - 1);
    }
  }
  return { laneOf, laneCount: lanes.length };
}

/**
 * Every cycle reachable in the flow graph, as the list of nodes on each.
 *
 * A questionnaire is a walk that has to terminate, so a cycle is always a defect — and
 * the editor can create one with two drags, since `connectionKind` only refuses a
 * self-loop and a duplicate edge. It was previously handled but never REPORTED: the
 * rank pass has a visit cap so a cycle cannot hang the layout, which meant a cyclic
 * draft rendered as if it were fine.
 *
 * Iterative DFS with colouring rather than recursion, so a pathological draft cannot
 * blow the stack.
 */
function findCycles(ids: string[], adj: Map<string, string[]>): string[][] {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Map<string, number>(ids.map((id) => [id, WHITE]));
  const cycles: string[][] = [];
  const seen = new Set<string>();

  for (const root of ids) {
    if (colour.get(root) !== WHITE) continue;
    const stack: { id: string; path: string[]; next: number }[] = [
      { id: root, path: [root], next: 0 },
    ];
    colour.set(root, GREY);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const succs = adj.get(top.id) ?? [];
      if (top.next >= succs.length) {
        colour.set(top.id, BLACK);
        stack.pop();
        continue;
      }
      const nxt = succs[top.next++];
      const c = colour.get(nxt);
      if (c === GREY) {
        // Back edge: the cycle is the tail of the current path from `nxt` onward.
        const at = top.path.indexOf(nxt);
        if (at >= 0) {
          const cycle = top.path.slice(at);
          // Canonicalise (rotate to the smallest id) so the same cycle found from two
          // roots is reported once.
          const min = cycle.indexOf([...cycle].sort()[0]);
          const key = [...cycle.slice(min), ...cycle.slice(0, min)].join('>');
          if (!seen.has(key)) {
            seen.add(key);
            cycles.push([...cycle.slice(min), ...cycle.slice(0, min)]);
          }
        }
      } else if (c === WHITE) {
        colour.set(nxt, GREY);
        stack.push({ id: nxt, path: [...top.path, nxt], next: 0 });
      }
    }
  }
  return cycles;
}

/** Condition shapes we can reason about when asking "could both hold at once?". */
type CondShape =
  | { k: 'always' }
  | { k: 'eq'; id: string; value: string }
  | { k: 'includes'; id: string; value: string }
  | { k: 'cmp'; id: string; op: '>' | '>=' | '<' | '<='; num: number }
  | { k: 'unknown' };

function shapeOf(expression: string): CondShape {
  const e = expression.trim();
  if (e === 'true') return { k: 'always' };
  const eq = /^metric\[\s*'([^']+)'\s*\]\s*={2,3}\s*'([^']*)'$/.exec(e);
  if (eq) return { k: 'eq', id: eq[1], value: eq[2] };
  const inc = /^metric\[\s*'([^']+)'\s*\]\.includes\(\s*'([^']*)'\s*\)$/.exec(e);
  if (inc) return { k: 'includes', id: inc[1], value: inc[2] };
  const cmp = /^metric\[\s*'([^']+)'\s*\]\s*(>=|<=|>|<)\s*(-?[\d.]+)$/.exec(e);
  if (cmp) return { k: 'cmp', id: cmp[1], op: cmp[2] as '>' | '>=' | '<' | '<=', num: Number(cmp[3]) };
  return { k: 'unknown' };
}

/**
 * Can two sibling conditions hold for the SAME submission?
 *
 * Only one edge out of a node is ever taken — the engine stops at the first rule that
 * evaluates true. So two conditions that can both hold are not a runtime ambiguity,
 * they are an AUTHORING one: which branch runs then depends on the order rather than on
 * what anyone decided, and the loser is invisible.
 *
 * Returns `unknown` rather than guessing when either side is a shape this cannot
 * reason about. A false "these are fine" is the worst of the three outcomes.
 */
export function siblingRelation(
  a: string,
  b: string,
  isMultiSelect: (qualifiedId: string) => boolean,
): 'disjoint' | 'overlap' | 'unknown' {
  const x = shapeOf(a);
  const y = shapeOf(b);
  if (x.k === 'always' || y.k === 'always') return 'overlap';
  if (x.k === 'unknown' || y.k === 'unknown') return 'unknown';

  if (x.k === 'eq' && y.k === 'eq') {
    if (x.id !== y.id) return 'overlap'; // different questions: both can be true
    return x.value === y.value ? 'overlap' : 'disjoint';
  }
  if (x.k === 'includes' && y.k === 'includes') {
    if (x.id !== y.id) return 'overlap';
    if (x.value === y.value) return 'overlap';
    // THIS is where multi-select matters: one answer can contain BOTH values, so two
    // `includes` on the same multi-select question are not mutually exclusive — the
    // live Q1.1.3.1 is exactly this shape. On a single-select question the answer is
    // one value, so two different `includes` cannot both hold.
    return isMultiSelect(x.id) ? 'overlap' : 'disjoint';
  }
  if (x.k === 'cmp' && y.k === 'cmp' && x.id === y.id) {
    const lo = (c: typeof x) => (c.op === '>' ? c.num + 1e-9 : c.op === '>=' ? c.num : -Infinity);
    const hi = (c: typeof x) => (c.op === '<' ? c.num - 1e-9 : c.op === '<=' ? c.num : Infinity);
    return Math.max(lo(x), lo(y)) <= Math.min(hi(x), hi(y)) ? 'overlap' : 'disjoint';
  }
  // Mixed shapes on the same question (an `includes` beside an `eq`, a range beside an
  // equality): not provably disjoint, and claiming otherwise would be the one
  // dangerous answer.
  if ('id' in x && 'id' in y && x.id !== y.id) return 'overlap';
  return 'unknown';
}

/**
 * Crossings between two adjacent rows, given the order of each.
 *
 * Two edges (u1→v1) and (u2→v2) cross when their endpoints are in opposite relative
 * order. Counted by brute force over edge pairs: the widest live row has two nodes, and
 * even a hand-built row of twenty is 400 comparisons.
 */
function crossingsBetween(
  upper: string[],
  lower: string[],
  pairs: { from: string; to: string }[],
): number {
  const posU = new Map(upper.map((id, i) => [id, i]));
  const posL = new Map(lower.map((id, i) => [id, i]));
  const es = pairs
    .filter((p) => posU.has(p.from) && posL.has(p.to))
    .map((p) => [posU.get(p.from)!, posL.get(p.to)!] as [number, number]);
  let n = 0;
  for (let i = 0; i < es.length; i++) {
    for (let j = i + 1; j < es.length; j++) {
      const [a1, b1] = es[i];
      const [a2, b2] = es[j];
      if ((a1 - a2) * (b1 - b2) < 0) n++;
    }
  }
  return n;
}

/** Total crossings across every adjacent row boundary. */
function totalCrossings(
  order: string[][],
  pairsByBoundary: { from: string; to: string }[][],
): number {
  let n = 0;
  for (let r = 0; r + 1 < order.length; r++) {
    n += crossingsBetween(order[r], order[r + 1], pairsByBoundary[r] ?? []);
  }
  return n;
}

/**
 * Median-heuristic ordering: the classic ordering phase of a layered drawing.
 *
 * Each sweep re-orders one row by the median position of each node's neighbours in the
 * fixed adjacent row, alternating downward and upward, keeping whichever intermediate
 * result had the fewest crossings. Nodes with no neighbour in the reference row keep
 * their current index, which stops them drifting to one end.
 *
 * Sweep count is fixed rather than run-to-convergence so the function is bounded and
 * deterministic — the layout's reproducibility matters more than squeezing out a last
 * crossing.
 */
function tidyOrder(
  initial: string[][],
  pairsByBoundary: { from: string; to: string }[][],
): string[][] {
  const SWEEPS = 8;
  let current = initial.map((r) => [...r]);
  let best = current.map((r) => [...r]);
  let bestScore = totalCrossings(best, pairsByBoundary);

  const reorder = (row: string[], ref: string[], edges: { a: string; b: string }[]) => {
    const posRef = new Map(ref.map((id, i) => [id, i]));
    const keyed = row.map((id, idx) => {
      const ns = edges.filter((e) => e.b === id).map((e) => posRef.get(e.a)!).filter((x) => x !== undefined);
      ns.sort((x, y) => x - y);
      // No neighbour → keep the current index, so it stays put rather than sorting to 0.
      const median = ns.length === 0 ? idx : ns[Math.floor(ns.length / 2)];
      return { id, median, idx };
    });
    keyed.sort((x, y) => x.median - y.median || x.idx - y.idx);
    return keyed.map((k) => k.id);
  };

  for (let sweep = 0; sweep < SWEEPS; sweep++) {
    const down = sweep % 2 === 0;
    if (down) {
      for (let r = 1; r < current.length; r++) {
        const edges = (pairsByBoundary[r - 1] ?? []).map((p) => ({ a: p.from, b: p.to }));
        current[r] = reorder(current[r], current[r - 1], edges);
      }
    } else {
      for (let r = current.length - 2; r >= 0; r--) {
        // Upward sweep: the reference is the row BELOW, so the edge direction flips.
        const edges = (pairsByBoundary[r] ?? []).map((p) => ({ a: p.to, b: p.from }));
        current[r] = reorder(current[r], current[r + 1], edges);
      }
    }
    const score = totalCrossings(current, pairsByBoundary);
    if (score < bestScore) {
      bestScore = score;
      best = current.map((r) => [...r]);
    }
  }
  return best;
}


/**
 * The graph facts every analysis needs, computed once.
 *
 * Extracted so the variable picker can reuse exactly the same dominator sets the
 * CHECKER uses. Two implementations of "which questions are guaranteed answered here"
 * would eventually disagree, and then the editor would offer a variable the checker
 * goes on to flag — the surface contradicting itself.
 */
export interface GraphFacts {
  nodes: QuestionnaireNode[];
  byNodeId: Map<string, QuestionnaireNode>;
  byQualifiedId: Map<string, QuestionnaireNode>;
  entryId: string | undefined;
  adj: Map<string, string[]>;
  preds: Map<string, string[]>;
  reachable: Set<string>;
  /** `dom.get(x)` = nodes on EVERY path from the entry to `x` (includes `x`). */
  dom: Map<string, Set<string>>;
  /** `pdom.get(x)` = nodes on every path from `x` to a terminal (includes `x`). */
  pdom: Map<string, Set<string>>;
}

export function graphFactsOf(def: QuestionnaireDefinition): GraphFacts {
  const nodes = def.nodes;
  const byNodeId = new Map(nodes.map((n) => [n.nodeId, n]));
  const entryId = entryNodeOf(nodes)?.nodeId;
  const byQualifiedId = new Map<string, QuestionnaireNode>();
  for (const n of nodes) if (n.qualifiedNodeId) byQualifiedId.set(n.qualifiedNodeId, n);

  const adj = new Map<string, string[]>();
  const preds = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.nodeId, []);
  for (const n of nodes) {
    for (const e of outEdgesOf(n)) {
      // Dangling targets must not enter the graph or the dominator passes would invent
      // a node that does not exist.
      if (!byNodeId.has(e.target)) continue;
      adj.get(n.nodeId)!.push(e.target);
      if (!preds.has(e.target)) preds.set(e.target, []);
      preds.get(e.target)!.push(n.nodeId);
    }
  }
  const reachable = reachableFrom(entryId, adj);
  const allIds = nodes.map((n) => n.nodeId);
  return {
    nodes,
    byNodeId,
    byQualifiedId,
    entryId,
    adj,
    preds,
    reachable,
    dom: dominators(entryId, allIds, preds, reachable),
    pdom: postDominators(allIds, adj, reachable),
  };
}

/**
 * Does `cut` cover every path to `owner`?
 *
 * Exported for the variable picker: it is the oracle for "is this group of fallback
 * questions enough", and the same one the `ref-group-uncovered` check uses.
 */
export function groupCoversNode(
  cut: Set<string>,
  owner: string,
  facts: GraphFacts,
  basis: 'before' | 'whole-walk',
): boolean {
  return cutCoversOwner(cut, owner, facts.entryId, facts.adj, facts.reachable, basis);
}

/**
 * Project one definition.
 *
 * Pure and synchronous: the same definition always yields the same coordinates and
 * the same issue list in the same order, which is what makes a draft-vs-live diff
 * meaningful and the tests worth writing.
 */
/**
 * What the canvas should draw.
 *
 * `grants: 'hide'` leaves the derived eligibility boxes out of the projection. The
 * mock has only question cards and connectors, and on the merged `q_all` the grant
 * column is 46 of the 76 boxes — it buries the thing the page is for.
 *
 * Nothing about the DATA changes: `programRules` stay on the nodes, the checker still
 * reads them (`unknown-program-name`, `ref-not-dominating` and the rest are all
 * program-rule findings), and the inspector still shows them. Only the boxes go.
 * Question positions are unaffected either way — a question's x is
 * `gutter + col * (NODE_W + COL_GAP)`, which never consulted the grant column.
 */
export interface ProjectOptions {
  grants?: 'show' | 'hide';
  /**
   * `grid`   — the hand-written left-packed slots. Default, and what every geometry
   *            test pins, because the draft-vs-live diff rests on its stability.
   * `tidy`   — d3-dag with LP-optimal coordinates: a parent is centred over its
   *            children, which is the mock's shape. Falls back to `grid` when the
   *            draft has a cycle, since d3-dag cannot lay one out and reporting the
   *            cycle is this surface's job.
   */
  layout?: 'grid' | 'tidy';
}

export function projectQuestionnaire(
  def: QuestionnaireDefinition,
  ordering: Ordering = 'source',
  opts: ProjectOptions = {},
): Projection {
  const showGrants = (opts.grants ?? 'show') === 'show';
  const wantTidy = (opts.layout ?? 'grid') === 'tidy';
  const nodes = def.nodes;
  const byNodeId = new Map(nodes.map((n) => [n.nodeId, n]));
  const entry = entryNodeOf(nodes);
  const entryId = entry?.nodeId;

  // Qualified-id → node, for resolving `metric[...]` refs back to a question.
  const byQualifiedId = new Map<string, QuestionnaireNode>();
  for (const n of nodes) if (n.qualifiedNodeId) byQualifiedId.set(n.qualifiedNodeId, n);

  const adj = new Map<string, string[]>();
  const preds = new Map<string, string[]>();
  for (const n of nodes) adj.set(n.nodeId, []);
  for (const n of nodes) {
    for (const e of outEdgesOf(n)) {
      // Dangling targets are reported below; they must not enter the graph or the
      // rank/dominator passes would invent a node that does not exist.
      if (!byNodeId.has(e.target)) continue;
      adj.get(n.nodeId)!.push(e.target);
      if (!preds.has(e.target)) preds.set(e.target, []);
      preds.get(e.target)!.push(n.nodeId);
    }
  }

  const reachable = reachableFrom(entryId, adj);
  const rank = longestRanks(entryId, adj, reachable);
  const allIds = nodes.map((n) => n.nodeId);
  const dom = dominators(entryId, allIds, preds, reachable);
  const pdom = postDominators(allIds, adj, reachable);

  const issues: Issue[] = [];
  const push = (i: Omit<Issue, 'id'>) =>
    issues.push({ ...i, id: `${i.code}:${i.nodeId ?? '-'}:${issues.length}` });

  // ── Structural checks ─────────────────────────────────────────────────────
  for (const n of nodes) {
    if (n.nextNodeId && (n.nextQuestionRules?.length ?? 0) > 0) {
      push({
        severity: 'error',
        code: 'both-edge-kinds',
        nodeId: n.nodeId,
        message: `「${n.nodeId}」同时设置了 nextNodeId 和 ${n.nextQuestionRules!.length} 条 nextQuestionRules —— 引擎只会走 nextNodeId（${n.nextNodeId}），条件边全部失效。`,
      });
    }
    for (const e of outEdgesOf(n)) {
      if (!byNodeId.has(e.target)) {
        push({
          severity: 'error',
          code: 'dangling-edge-target',
          nodeId: n.nodeId,
          message: `「${n.nodeId}」的出边指向不存在的节点「${e.target}」。`,
        });
      }
    }
    if (!n.isLastNode && outEdgesOf(n).length === 0) {
      push({
        severity: 'error',
        code: 'dead-end',
        nodeId: n.nodeId,
        message: `「${n.nodeId}」不是终点节点，但没有任何出边 —— 走到这里问卷会停住。`,
      });
    }
    if (entryId && !reachable.has(n.nodeId)) {
      push({
        severity: 'warning',
        code: 'unreachable',
        nodeId: n.nodeId,
        message: `「${n.nodeId}」从入口节点不可达，永远不会被问到。`,
      });
    }
    if (!n.qualifiedNodeId) {
      push({
        severity: 'error',
        code: 'missing-qualified-id',
        nodeId: n.nodeId,
        message: `「${n.nodeId}」没有 qualifiedNodeId —— 它的答案无法进入 metric 上下文，所有引用它的规则都不会命中。`,
      });
    }
  }

  // ── Rule checks ───────────────────────────────────────────────────────────
  /**
   * `basis` is when the expression runs, which decides which answers exist:
   *  - 'before'     — navigation / auto-answer, evaluated standing on the node.
   *  - 'whole-walk' — program rules, evaluated at submit with the full answer set.
   * See {@link postDominators} for why conflating the two produces false positives.
   */
  const checkExpression = (
    owner: QuestionnaireNode,
    expression: string,
    where: string,
    basis: 'before' | 'whole-walk',
  ) => {
    // Refs that are not individually guaranteed. Collected first, then judged as a
    // GROUP, because a hand-written `a || b` fallback is only sound as a group.
    const ungrounded: QuestionnaireNode[] = [];
    for (const ref of metricRefsOf(expression)) {
      const refNode = byQualifiedId.get(ref);
      if (!refNode) {
        push({
          severity: 'error',
          code: 'unknown-metric-ref',
          nodeId: owner.nodeId,
          message: `${where} 引用了 metric['${ref}']，但本问卷里没有 qualifiedNodeId 等于它的问题。`,
        });
        continue;
      }
      const guaranteed =
        (dom.get(owner.nodeId)?.has(refNode.nodeId) ?? false) ||
        (basis === 'whole-walk' && (pdom.get(owner.nodeId)?.has(refNode.nodeId) ?? false));
      if (reachable.has(owner.nodeId) && !guaranteed) ungrounded.push(refNode);
      // Arithmetic / ordering against a TEXT node is the `dataType` trap: without
      // NUMBER, AnswerDataUtil hands the expression a String and the comparison
      // silently becomes lexicographic.
      const numericOp = new RegExp(
        `metric\\[\\s*'${ref.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}'\\s*\\]\\s*(>=|<=|>|<|\\*|\\+|-|/)`,
      );
      if (numericOp.test(expression) && (refNode.dataType ?? 'TEXT') !== 'NUMBER') {
        push({
          severity: 'warning',
          code: 'numeric-op-on-text-node',
          nodeId: owner.nodeId,
          message: `${where} 对 metric['${ref}'] 做了数值运算，但「${refNode.nodeId}」的 dataType 是 ${refNode.dataType ?? 'TEXT'} —— 答案不会转成数字，比较会退化成字符串比较。`,
        });
      }
    }

    // Judge the ungrounded refs as one fallback group (see cutCoversOwner). Only an
    // expression that actually alternates can be a fallback: without `||` the refs
    // are being AND-ed or arithmetically combined, so "one of them was asked" does
    // not save it.
    if (ungrounded.length) {
      const cut = new Set(ungrounded.map((n) => n.nodeId));
      const covered =
        expression.includes('||') &&
        cutCoversOwner(cut, owner.nodeId, entryId, adj, reachable, basis);
      if (!covered) {
        const names = ungrounded.map((n) => `metric['${n.qualifiedNodeId}']`).join('、');
        push({
          severity: 'warning',
          code: ungrounded.length > 1 ? 'ref-group-uncovered' : 'ref-not-dominating',
          nodeId: owner.nodeId,
          message:
            `${where} 引用了 ${names}，而${basis === 'before' ? `到达「${owner.nodeId}」时这些问题并非必定已答` : `它们不在经过「${owner.nodeId}」的所有路径上`}，表达式里也没有 || 兜底 —— 走另一条分支时取值为 undefined。` +
            `若实际是靠同一条表达式里的前置条件保证的（例如先判 metric['${entryNodeOf(nodes)?.qualifiedNodeId ?? '…'}']，再读只有该分支才会问到的字段），那这条是安全的，但本检查器不解析表达式语义、无法确认 —— 需要人工确认，并建议把这种隐式耦合显式化。`,
        });
      }
    }

    for (const name of hasKeyRefsOf(expression)) {
      if (!PROGRAM_NAMES.includes(name)) {
        const near = PROGRAM_NAMES.find((p) => p.toLowerCase() === name.toLowerCase());
        push({
          severity: 'error',
          code: 'unknown-program-name',
          nodeId: owner.nodeId,
          message: near
            ? `${where} 里 hasKey('${name}') 的大小写与项目名不一致，正确写法是「${near}」—— 当前这条永远为 false。`
            : `${where} 里 hasKey('${name}') 不是已知项目名，这条永远为 false。`,
        });
      }
    }
  };

  for (const n of nodes) {
    (n.nextQuestionRules ?? []).forEach((r, i) =>
      checkExpression(n, r.expression, `「${n.nodeId}」的第 ${i + 1} 条条件边`, 'before'),
    );
    (n.programRules ?? []).forEach((r, i) => {
      checkExpression(n, r.expression, `「${n.nodeId}」的第 ${i + 1} 条资格规则`, 'whole-walk');
      for (const v of r.values) {
        if (!PROGRAM_NAMES.includes(v)) {
          push({
            severity: 'error',
            code: 'unknown-program-name',
            nodeId: n.nodeId,
            message: `「${n.nodeId}」的第 ${i + 1} 条资格规则产出了未知项目名「${v}」，推荐引擎按名字查 InvestmentProgramDetail，查不到就等于这条规则没有效果。`,
          });
        }
      }
    });
    // autoAnswerRules are browser-only today, so their refs are checked with the
    // same dominance basis but reported as the client-side rules they are.
    (n.autoAnswerRules ?? []).forEach((r, i) =>
      checkExpression(
        n,
        r.expression,
        `「${n.nodeId}」的第 ${i + 1} 条自动作答规则（仅前端生效）`,
        'before',
      ),
    );
  }

  for (const n of nodes) {
    const rules = n.nextQuestionRules ?? [];
    const catchAllAt = rules.findIndex((r) => r.expression.trim() === 'true');
    rules.forEach((r, i) => {
      const expr = r.expression.trim();
      const firstSameAt = rules.findIndex((x) => x.expression.trim() === expr);
      if (catchAllAt >= 0 && i > catchAllAt) {
        push({
          severity: 'error',
          code: 'dead-branch',
          nodeId: n.nodeId,
          message: `「${n.nodeId}」的第 ${i + 1} 条条件边排在默认分支（第 ${catchAllAt + 1} 条，条件为 true）之后 —— 条件边是第一个为 true 的胜出，所以它永远不会被取用。把默认分支移到最后。`,
        });
      } else if (firstSameAt < i) {
        push({
          severity: 'warning',
          code: 'dead-branch',
          nodeId: n.nodeId,
          message: `「${n.nodeId}」的第 ${i + 1} 条条件边与第 ${firstSameAt + 1} 条条件完全相同，永远轮不到它 —— 前一条已经命中。`,
        });
      }
    });
  }

  // ── Whole-topology checks ─────────────────────────────────────────────────
  // A questionnaire is a walk that must terminate, so any cycle is a defect. Two
  // drags can create one: connectionKind only refuses a self-loop and a duplicate.
  for (const cycle of findCycles(allIds, adj)) {
    push({
      severity: 'error',
      code: 'cycle',
      nodeId: cycle[0],
      message: `存在环：${cycle.join(' → ')} → ${cycle[0]} —— 问卷必须能走到终点，走进这个环就出不来了。断掉其中一条边。`,
    });
  }

  // Only ONE outgoing edge is ever taken (first-match-wins). So two sibling
  // conditions that can hold together are an authoring ambiguity: which branch runs
  // depends on the order, not on anyone's decision, and the loser is invisible.
  for (const n of nodes) {
    const rules = n.nextQuestionRules ?? [];
    if (rules.length < 2) continue;
    const isMulti = (qid: string) => !!byQualifiedId.get(qid)?.multiSelect;
    for (let i = 0; i < rules.length; i++) {
      for (let j = i + 1; j < rules.length; j++) {
        // An identical pair, or anything paired with a catch-all, is already covered:
        // identical conditions and rules sitting behind a `true` are reported as dead
        // branches, so saying it again here would just add noise.
        //
        // The catch-all must be skipped on BOTH sides, not just as `rules[i]`.
        // `siblingRelation` answers 'overlap' whenever either operand is `always`, so
        // the ordinary shape — real conditions first, `true` last — made every real
        // condition "overlap" the trailing default and warned about a node that is
        // authored exactly right. It went unnoticed because none of the five live
        // questionnaires ends in a `true` branch; every branch added through the
        // editor does.
        if (rules[i].expression.trim() === rules[j].expression.trim()) continue;
        if (rules[i].expression.trim() === 'true' || rules[j].expression.trim() === 'true')
          continue;
        const rel = siblingRelation(rules[i].expression, rules[j].expression, isMulti);
        if (rel === 'overlap') {
          push({
            severity: 'warning',
            code: 'branch-overlap',
            nodeId: n.nodeId,
            message: `「${n.nodeId}」的第 ${i + 1} 和第 ${j + 1} 条条件边可能同时成立 —— 同一次提交只会走一条（第一个为 true 的胜出），所以此时走哪条由顺序决定，而不是由设计决定；第 ${j + 1} 条会被静默跳过。要么把条件改成互斥，要么确认这个优先级就是你要的。`,
          });
        } else if (rel === 'unknown') {
          push({
            severity: 'info',
            code: 'branch-overlap-unknown',
            nodeId: n.nodeId,
            message: `「${n.nodeId}」的第 ${i + 1} 和第 ${j + 1} 条条件边是否互斥，静态判断不出来（表达式形式超出可分析范围）。同一次提交只会走一条，请人工确认不会同时成立。`,
          });
        }
      }
    }
  }

  const issuesByNode = new Map<string, string[]>();
  for (const i of issues) {
    if (!i.nodeId) continue;
    if (!issuesByNode.has(i.nodeId)) issuesByNode.set(i.nodeId, []);
    issuesByNode.get(i.nodeId)!.push(i.id);
  }

  // ── Layout ────────────────────────────────────────────────────────────────
  // Group by rank, then order within a rank by the node's index in the source array.
  // Source order is stable across edits that do not touch it, so a node does not
  // jump columns because an unrelated sibling was added.
  const indexOf = new Map(nodes.map((n, i) => [n.nodeId, i]));
  const rankOf = (id: string) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;
  const byRank = new Map<number, QuestionnaireNode[]>();
  for (const n of nodes) {
    // Unreachable nodes have no rank; park them in a rank past everything else so
    // they are visible without disturbing the spine.
    const r = reachable.has(n.nodeId) ? rankOf(n.nodeId) : -1;
    if (!byRank.has(r)) byRank.set(r, []);
    byRank.get(r)!.push(n);
  }
  const orphanRank = Math.max(0, ...[...byRank.keys()].filter((k) => k >= 0)) + 1;
  const rows = [...byRank.entries()]
    .map(([r, ns]) => [r < 0 ? orphanRank : r, ns] as [number, QuestionnaireNode[]])
    .sort((a, b) => a[0] - b[0]);

  // Column order within each row. `source` keeps definition order (stable under
  // edits); `tidy` runs crossing reduction over it.
  const sourceOrder = rows.map(([, rowNodes]) =>
    [...rowNodes].sort((a, b) => indexOf.get(a.nodeId)! - indexOf.get(b.nodeId)!).map((n) => n.nodeId),
  );
  const rowIndexOf = new Map<string, number>();
  sourceOrder.forEach((ids, r) => ids.forEach((id) => rowIndexOf.set(id, r)));
  // Only ADJACENT-row edges participate: a skipping edge is routed in a gutter lane
  // and never crosses the spine, so letting it pull on the ordering would be wrong.
  const pairsByBoundary: { from: string; to: string }[][] = sourceOrder.map(() => []);
  for (const n of nodes) {
    for (const e of outEdgesOf(n)) {
      if (!byNodeId.has(e.target)) continue;
      const a = rowIndexOf.get(n.nodeId);
      const b = rowIndexOf.get(e.target);
      if (a === undefined || b === undefined) continue;
      if (b - a === 1) pairsByBoundary[a].push({ from: n.nodeId, to: e.target });
    }
  }
  const columnOrder =
    ordering === 'tidy' ? tidyOrder(sourceOrder, pairsByBoundary) : sourceOrder;
  const crossings = totalCrossings(columnOrder, pairsByBoundary);
  const colIndexOf = new Map<string, number>();
  columnOrder.forEach((ids) => ids.forEach((id, c) => colIndexOf.set(id, c)));

  const out: CpaNode[] = [];
  const edges: CpaEdge[] = [];
  // Widest rank, computed BEFORE placing anything: derived nodes park to the right of
  // the whole spine, not just to the right of their own owner. Placing them relative
  // to the owner put q_1_1's grant column on top of the China branch (Q1.1.2_b*),
  // which share rank 1 with the Global branch.
  const spineCols = Math.max(1, ...rows.map(([, ns]) => ns.length));

  // Which drawn row each node ends up on, needed before any edge is built so a
  // skipping edge can be detected and given a lane.
  const rowOfNode = new Map<string, number>();
  rows.forEach(([, rowNodes], rowIdx) => {
    for (const n of rowNodes) rowOfNode.set(n.nodeId, rowIdx);
  });

  // Flow edges that jump a row. A straight line would run under the cards in between,
  // so each gets a lane in a gutter reserved on the LEFT (the right is already taken
  // by the derived grant column).
  const skipSpans: { id: string; from: number; to: number }[] = [];
  for (const n of nodes) {
    outEdgesOf(n).forEach((e, i) => {
      if (!byNodeId.has(e.target)) return;
      const from = rowOfNode.get(n.nodeId);
      const to = rowOfNode.get(e.target);
      if (from === undefined || to === undefined) return;
      if (Math.abs(to - from) > 1) {
        skipSpans.push({ id: `e:${n.nodeId}->${e.target}:${i}`, from, to });
      }
    });
  }
  const { laneOf, laneCount } = assignLanes(skipSpans);
  // Gutter only exists when something needs it, so the common all-adjacent tree is
  // laid out exactly as before.
  const gutter = laneCount > 0 ? (laneCount + 1) * LANE_GAP : 0;
  /*
   * `tidy` replaces only the CARD COORDINATES. Rows, ordering, detour lanes, crossing
   * counts and every check above are untouched — this is the one phase the hand-written
   * layout got visibly wrong (a parent above its leftmost child instead of above the
   * middle of its children), and it is the one phase a library does better.
   *
   * `null` means d3-dag refused the graph — in practice a cycle, which a draft is
   * allowed to have. Falling back to the grid keeps the editor usable while the cycle
   * error is on screen.
   */
  const sizeOf = new Map(nodes.map((n) => [n.nodeId, cardSize(n)] as const));
  const widthOf = (id: string) => sizeOf.get(id)?.width ?? NODE_W;
  const heightOf = (id: string) => sizeOf.get(id)?.height ?? NODE_H;
  const tidy = wantTidy
    ? dagLayout({
        ids: nodes.map((n) => n.nodeId),
        edges: nodes.flatMap((n) =>
          outEdgesOf(n).map((e) => ({ source: n.nodeId, target: e.target })),
        ),
        // Both per node now: a card's width comes from its answers and its height from
        // the wrapped question plus those answers.
        nodeW: widthOf,
        nodeH: heightOf,
        colGap: COL_GAP,
        rowGap: ROW_GAP,
        // The order the rest of this function already decided on, so d3-dag's
        // orientation cannot contradict rule order on screen.
        orderHint: columnOrder,
      })
    : null;

  /*
   * Right edge of the question area, which is where the derived grant column starts.
   *
   * `spineCols` is a GRID measure — columns times a fixed pitch. In tidy mode the x
   * coordinates come from d3-dag and the graph is wider than that (centring spreads
   * it), so reusing the grid estimate put the grant column on top of the question
   * cards. Only reachable through `{ grants: 'show', layout: 'tidy' }` — the canvas
   * hides grants today — but it is reachable, so it is computed from the real extent.
   */
  const questionsRight = tidy
    ? Math.max(0, ...[...tidy.pos.entries()].map(([id, p]) => gutter + p.x + widthOf(id)))
    : gutter + spineCols * (NODE_W + COL_GAP);
  const grantColumnX = questionsRight + COL_GAP;

  for (const [r, rowNodes] of rows) {
    const ordered = [...rowNodes].sort(
      (a, b) => (colIndexOf.get(a.nodeId) ?? 0) - (colIndexOf.get(b.nodeId) ?? 0),
    );
    // Grid mode walks the row accumulating real widths; a fixed pitch would overlap
    // now that cards differ in width.
    let cursor = gutter;
    ordered.forEach((n, col) => {
      const slot = tidy?.pos.get(n.nodeId);
      const w = widthOf(n.nodeId);
      const x = slot ? gutter + slot.x : cursor;
      const y = slot ? slot.y : r * (NODE_H + ROW_GAP);
      if (!slot) cursor += w + COL_GAP;
      void col;
      const h = heightOf(n.nodeId);
      const size = sizeOf.get(n.nodeId);
      const layer = tidy?.layerOf.get(n.nodeId) ?? r;
      out.push({
        id: n.nodeId,
        kind: 'question',
        derived: false,
        position: { x, y },
        height: h,
        width: w,
        optionRows: size?.optionRows ?? 0,
        shownOptions: size?.shownOptions ?? 0,
        hiddenOptions: size?.hiddenOptions ?? 0,
        layer,
        data: {
          ownerNodeId: n.nodeId,
          title: n.questionText.cn || n.questionText.en,
          subtitle: n.qualifiedNodeId,
          questionKind: questionKindOf(n),
          node: n,
          issueIds: issuesByNode.get(n.nodeId) ?? [],
        },
      });

      // Derived grant nodes, stacked to the right of their owner.
      if (!showGrants) return;
      (n.programRules ?? []).forEach((rule, i) => {
        const id = `${n.nodeId}::grant::${i}`;
        out.push({
          id,
          kind: 'grant',
          derived: true,
          height: NODE_H,
          width: GRANT_W,
          optionRows: 0,
          shownOptions: 0,
          hiddenOptions: 0,
          layer,
          position: {
            // One lane per owner column so two owners in the same rank do not stack
            // their grants on each other.
            x: grantColumnX + col * (GRANT_W + COL_GAP),
            y: y + i * (NODE_H * 0.6),
          },
          data: {
            ownerNodeId: n.nodeId,
            title: rule.values.join(' + '),
            subtitle: rule.expression,
            ruleIndex: i,
            rule,
            issueIds: [],
          },
        });
        edges.push({ id: `e:${id}`, source: n.nodeId, target: id, kind: 'grant' });
      });
    });
  }

  for (const n of nodes) {
    outEdgesOf(n).forEach((e, i) => {
      if (!byNodeId.has(e.target)) return;
      const id = `e:${n.nodeId}->${e.target}:${i}`;
      const from = rowOfNode.get(n.nodeId);
      const to = rowOfNode.get(e.target);
      const rowSpan = from !== undefined && to !== undefined ? Math.abs(to - from) : undefined;
      const lane = laneOf.get(id);
      const rules = n.nextQuestionRules ?? [];
      const isConditional = e.kind === 'when' && !!e.label;
      // First catch-all shadows everything after it; an exact repeat of an earlier
      // condition is shadowed by that earlier one.
      const catchAllAt = rules.findIndex((r) => r.expression.trim() === 'true');
      const firstSameAt = isConditional
        ? rules.findIndex((r) => r.expression.trim() === (e.label ?? '').trim())
        : -1;
      edges.push({
        id,
        source: n.nodeId,
        target: e.target,
        kind: e.kind,
        label: e.label,
        rowSpan,
        lane,
        // Lane 0 sits closest to the spine; each further lane steps left.
        laneX: lane === undefined ? undefined : gutter - (lane + 1) * LANE_GAP,
        ruleIndex: isConditional ? i + 1 : undefined,
        ruleCount: isConditional ? rules.length : undefined,
        displayLabel: isConditional
          ? humanCondition(e.label!, (qid, value) => {
              const owner = byQualifiedId.get(qid);
              const opt = owner?.answerOptions?.find((o) => o.value === value);
              return opt ? opt.optionText.cn || opt.optionText.en : undefined;
            })
          : undefined,
        isDefaultBranch: isConditional && e.label!.trim() === 'true',
        isDeadBranch:
          isConditional &&
          ((catchAllAt >= 0 && i > catchAllAt) || (firstSameAt >= 0 && firstSameAt < i)),
      });
    });
  }

  const maxX = Math.max(0, ...out.map((n) => n.position.x + n.width));
  const maxY = Math.max(0, ...out.map((n) => n.position.y + NODE_H));

  return {
    nodes: out,
    edges,
    issues,
    entryNodeId: entryId,
    width: maxX,
    height: maxY,
    crossings,
  };
}
